/**
 * accountKey.js — "does this user have a Binance ACCOUNT key on our server"
 * (owner 10.10.2026). Keys live on the server (Account → Exchange API,
 * PUT /account-key) — never in the browser. Everything on the site that
 * reads the user's own Binance account (journal, positions, calculator
 * balance) signs through POST /binance/stored with that key; the panels
 * pass `STORED` where they used to pass a key read from localStorage
 * (`cyber_binance_*`, which nothing has written since keys moved to the
 * server — the journal said "no keys" with a key stored).
 */
import { useEffect, useState } from 'react';
import { api, getToken } from '../api/client';

/** Pass as apiKey/apiSecret to binanceSignedFetch: sign on the server with the stored key. */
export const STORED = '@stored';
/** The labels the server reads as the account key (apps/server/src/lib/accountKey.js). */
const ACCOUNT_LABELS = new Set(['account', 'terminal', 'site']);

let has = false;
let loaded = false;
let inflight = null;
const subs = new Set();

/** Re-read from the server — call after connecting / disconnecting a key. */
export function refreshAccountKey() {
  if (!getToken()) { set(false); return Promise.resolve(false); }
  inflight ??= api('/bot-keys')
    .then((keys) => { loaded = true; return set((keys || []).some((k) => k.exchange === 'BINANCE' && !k.testnet && ACCOUNT_LABELS.has(k.label))); })
    .catch(() => has) // a blip keeps the last answer
    .finally(() => { inflight = null; });
  return inflight;
}

function set(v) {
  if (v !== has) { has = v; subs.forEach((f) => f(v)); }
  return v;
}

export const hasAccountKey = () => has;

/** `{ api, secret }` for binanceSignedFetch, or null when no key is stored. */
export const binanceCreds = () => (has ? { api: STORED, secret: STORED } : null);

/** Re-renders when the answer changes; fetches once per page. */
export function useAccountKey() {
  const [v, setV] = useState(has);
  useEffect(() => {
    subs.add(setV);
    if (!loaded) void refreshAccountKey();
    return () => { subs.delete(setV); };
  }, []);
  return v;
}
